import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Link2, Megaphone, Crown, Share2, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

// Per-tier branded art. Each waitlist milestone gets a distinct glyph + accent so hitting it is
// genuinely worth posting on X / Telegram. Grad classes are string LITERALS so Tailwind JIT keeps them.
const TIER_ART = {
  Connector: { Icon: Link2, accent: "#3591F4", grad: "from-[#0a1526] via-[#07070B] to-[#0a1020]", tagline: "Wiring the trust network" },
  Amplifier: { Icon: Megaphone, accent: "#7c5cff", grad: "from-[#150e2b] via-[#07070B] to-[#0d0a1e]", tagline: "Amplifying the signal" },
  "Founding Sharer": { Icon: Crown, accent: "#ffd166", grad: "from-[#1c1608] via-[#07070B] to-[#0b1a17]", tagline: "Founding tier — top of the line" },
};

function Stat({ label, value, accent }) {
  return (
    <div className="flex items-baseline gap-2" data-testid={`milestone-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <span className="font-heading text-xl font-black leading-none" style={{ color: accent }}>{value}</span>
      <span className="text-[11px] uppercase tracking-wider text-white/50">{label}</span>
    </div>
  );
}

// One-tap, post-worthy image card for a waitlist referral milestone. Renders a branded card + QR and
// exports it to a PNG shared via the Web Share API (image + link) with a download+copy fallback.
export function MilestoneShareCard({ milestone, code, refCount = 0, spots = 10, position, link }) {
  const art = TIER_ART[milestone.name] || TIER_ART.Connector;
  const { Icon, accent } = art;
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const jumped = (refCount * spots).toLocaleString();

  const shareCard = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#07070B" });
      const text = `I just hit ${milestone.emoji} ${milestone.name} on the BLOQ Talk waitlist — the trust-gated Web3 messenger. Join with my link and skip the line: ${link}`;
      const blob = await (await fetch(dataUrl)).blob();
      const filename = `bloqtalk-${milestone.name.toLowerCase().replace(/\s+/g, "-")}.png`;
      const file = new File([blob], filename, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, title: `BLOQ Talk — ${milestone.name}` });
      } else {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = filename;
        a.click();
        try {
          await navigator.clipboard.writeText(link);
        } catch {
          /* ignore */
        }
        toast.success("Card saved — link copied. Post it on X or Telegram!");
      }
    } catch {
      toast.error("Couldn't generate the card — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4" data-testid="milestone-share-card">
      <div
        ref={cardRef}
        className={cn("relative overflow-hidden rounded-3xl border bg-gradient-to-br p-6 text-left", art.grad)}
        style={{ borderColor: `${accent}55` }}
        data-testid="milestone-card-visual"
      >
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full opacity-30 blur-3xl" style={{ background: accent }} />

        <div className="relative flex items-center justify-between">
          <div className="font-heading text-lg font-black tracking-tight text-white">
            BLOQ<span style={{ color: accent }}>Talk</span>
          </div>
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/50">Waitlist</span>
        </div>

        <div className="relative mt-5 flex items-center gap-4">
          <span
            className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border"
            style={{ borderColor: `${accent}66`, background: `${accent}1f`, color: accent }}
          >
            <Icon className="h-8 w-8" />
          </span>
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wider text-white/50">Milestone unlocked</div>
            <div className="font-heading text-3xl font-black leading-none text-white">
              {milestone.name} <span className="align-middle text-2xl">{milestone.emoji}</span>
            </div>
            <div className="mt-1 text-xs" style={{ color: accent }}>{art.tagline}</div>
          </div>
        </div>

        <div className="relative mt-5 flex items-end justify-between gap-4">
          <div className="space-y-1.5">
            <Stat label="Friends joined" value={refCount.toLocaleString()} accent={accent} />
            <Stat label="Spots jumped" value={jumped} accent={accent} />
            {typeof position === "number" && <Stat label="My position" value={`#${position.toLocaleString()}`} accent={accent} />}
          </div>
          <div className="shrink-0 rounded-xl bg-white p-2">
            <QRCodeCanvas value={link} size={78} bgColor="#ffffff" fgColor="#07070B" level="M" />
          </div>
        </div>

        <div className="relative mt-5 rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-center text-xs text-white/90">
          Skip the line — join with <span className="font-mono font-bold" style={{ color: accent }}>{code}</span>
        </div>
      </div>
      <button
        onClick={shareCard}
        disabled={busy}
        className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-xl bloq-gradient text-sm font-bold text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-60"
        data-testid="milestone-share-btn"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Share my {milestone.name} card
      </button>
    </div>
  );
}
